import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { controlToastState, Modal } from "risa-oasis-ui_v2";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { useFileToBase64 } from "../../../pages/finalDocuments/hooks";
import { resetFileUploadState } from "../../../redux/slice/fileUploadSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { AddMore } from "../../../svg/add-more";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import NewFileUploaderCard from "../../newFileUploader/newFileUploader";

interface DocumentsModalProps {
  onClose: () => void;
  documentsName: string[];
  documentsList: File[];
  id: string;
  newDocumentNames: (url: string[]) => void;
  newFiles: (files: File[]) => void;
  patientId?: string;
  orderId?: string;
  orderData?: MedicalPaOrder;
  onMergePdfs?: (base64Urls: string[], fileName?: string) => Promise<void>;
  isMerging?: boolean;
}

const DocumentsModal = (props: DocumentsModalProps) => {
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading] = useState<boolean>(false);
  const { progress } = useSelector((state: RootState) => state.fileUpload);
  const dispatch = useDispatch<AppDispatch>();

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  // Convert uploaded files to base64 only when needed (not on every render)
  const { convertFilesToBase64, clearBase64Urls } = useFileToBase64(
    undefined,
    false,
  );

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);
    const duplicateFiles = newFiles.filter((newFile) =>
      uploadedFiles.some((existingFile) => existingFile.name === newFile.name),
    );

    if (duplicateFiles.length > 0) {
      controlToastState("duplicate-file-upload");
      return;
    }

    const uniqueFiles = newFiles.filter(
      (newFile) =>
        !uploadedFiles.some(
          (existingFile) => existingFile.name === newFile.name,
        ),
    );

    apiWorkerManager.enqueueAndProcess({
      patient_id: props.patientId ?? "",
      order_id: props.orderId ?? "",
      org_id: getOrgIdForFetchExternalWorklist() ?? "",
      event_name: EventsName.ADD_MORE_DOCUMENTS_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
        documents_name: uniqueFiles.map((file) => file.name).join(","),
        payer_name: payerName,
      },
    });

    setFileNames((prev) => [...prev, ...uniqueFiles.map((file) => file.name)]);
    setUploadedFiles((prev) => [...prev, ...uniqueFiles]);
  };

  const handleOnSave = async () => {
    props.newDocumentNames(fileNames);
    props.newFiles(uploadedFiles);
    apiWorkerManager.enqueueAndProcess({
      patient_id: props.patientId ?? "",
      order_id: props.orderId ?? "",
      org_id: getOrgIdForFetchExternalWorklist() ?? "",
      event_name: EventsName.SAVE_BUTTON_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
        source: ModalNamesForBigQuery.FINAL_ATTACHMENTS_MODAL,
        auth_status: props?.orderData?.auth_on_file?.auth_status ?? "",
        payer_name: payerName,
      },
    });

    // If multiple files and merge function is provided, merge them
    if (props.onMergePdfs && uploadedFiles.length > 1) {
      const patientName =
        props.orderData?.demographics?.first_name &&
        props.orderData?.demographics?.last_name
          ? `${props.orderData.demographics.first_name}_${props.orderData.demographics.last_name}`
          : "patient";
      const mergedFileName = `${patientName}_merged_documents.pdf`;

      try {
        // Convert files to base64 only when needed (right before merge)
        const convertedBase64Urls = await convertFilesToBase64(uploadedFiles);

        // Call merge API with the converted URLs
        await props.onMergePdfs(convertedBase64Urls, mergedFileName);

        // Clear base64 URLs from memory after successful merge to free up memory
        clearBase64Urls();
      } catch (error) {
        console.error("Error merging PDFs:", error);
        // Fallback to saving individual files if merge fails
        controlToastState("error-merging-pdfs");
        props.newDocumentNames(fileNames);
        props.newFiles(uploadedFiles);
        // Clear base64 URLs even on error
        clearBase64Urls();
      }
    } else {
      // Single file or no merge function provided, just update parent state
      props.newDocumentNames(fileNames);
      props.newFiles(uploadedFiles);
    }

    props.onClose();
  };

  const handleClose = () => {
    dispatch(resetFileUploadState());
    props.onClose();
  };

  const handleFileDelete = (index: number) => {
    setUploadedFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });

    setFileNames((prevFileNames) => {
      const newFileNames = [...prevFileNames];
      newFileNames.splice(index, 1);
      return newFileNames;
    });

    apiWorkerManager.enqueueAndProcess({
      patient_id: props.patientId ?? "",
      order_id: props.orderId ?? "",
      org_id: getOrgIdForFetchExternalWorklist() ?? "",
      event_name: EventsName.DELETE_DOCUMENT_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
        documents_name: fileNames[index],
        payer_name: payerName,
      },
    });
  };

  useEffect(() => {
    setFileNames(
      props.documentsName.length > 0
        ? props.documentsName
        : ["Clinical attachments"],
    );
    setUploadedFiles(props.documentsList);
  }, []);

  return (
    <Modal
      dialogId={"documents-modal"}
      onSave={handleOnSave}
      title={"Attachments"}
      saveButtonText={props.isMerging ? "Merging PDFs..." : "Save"}
      cancelText={"Cancel"}
      onClose={handleClose}
      heightPercentage={70}
      disableSave={props.isMerging || false}
    >
      <div className="documents-modal--container flex flex-col gap-3">
        {fileNames.map((docName, index) => (
          <NewFileUploaderCard
            key={`${docName}-${index}`}
            fileName={docName}
            progress={progress}
            isUploading={isUploading}
            handleDelete={() => handleFileDelete(index)}
          />
        ))}
        <div
          className="documents-modal--add-more mt-2 flex items-center gap-1 hover:cursor-pointer"
          onClick={handleClick}
        >
          <AddMore />
          <div className="documents-modal--add-more--text text-h12 font-semibold text-tertiaryBlue-4">
            Add More
          </div>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            className="hidden"
            onChange={handleFileChange}
            accept=".pdf"
          />
        </div>
      </div>
    </Modal>
  );
};

export default DocumentsModal;
